import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ImageUpload } from '@/components/ImageUpload';
import { PhotoSourceTabs } from '@/components/PhotoSourceTabs';
import { PhotoLibraryPicker } from '@/components/PhotoLibraryPicker';
import { usePhotoLibrary } from '@/hooks/usePhotoLibrary';
import { isLibraryUrl } from '@/lib/photoLibraryTypes';
import { CategoryDetailsFields } from '@/components/CategoryDetailsFields';
import { POWER_SOURCES, CIRCULATION_MODES } from '@/lib/constants';
import {
  isToolCategory,
  ITEM_CATEGORY_OPTIONS,
  findItemCategoryByText,
  type ItemCategory,
} from '@/lib/itemCategoryConfig';
import {
  createEmptyCategoryDetailsState,
  deleteCategoryDetails,
  loadCategoryDetails,
  saveCategoryDetails,
  validateCategoryDetails,
  type CategoryDetailsState,
} from '@/lib/itemCategoryDetails';
import { useUpdateItem } from '@/hooks/useItems';
import type { ItemWithOwner, CirculationMode, PowerSource } from '@/hooks/useItems';

interface EditItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ItemWithOwner | null;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.item
 */
export function EditItemDialog({ open, onOpenChange, item }: EditItemDialogProps) {
  const updateItem = useUpdateItem();

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState<ItemCategory | ''>('');
  const [editBrand, setEditBrand] = useState('');
  const [editPowerSource, setEditPowerSource] = useState<PowerSource | ''>('');
  const [editPhotoUrl, setEditPhotoUrl] = useState<string | null>(null);
  const [editCirculationMode, setEditCirculationMode] = useState<CirculationMode>(
    'RETURN_TO_OWNER_REQUIRED',
  );
  const [editCategoryDetails, setEditCategoryDetails] = useState<CategoryDetailsState>(
    createEmptyCategoryDetailsState(),
  );
  const [originalCategory, setOriginalCategory] = useState<ItemCategory | ''>('');
  const [additionalDetailsOpen, setAdditionalDetailsOpen] = useState(true);
  const [photoTab, setPhotoTab] = useState<'upload' | 'library'>('upload');

  const { allItems: libraryItems } = usePhotoLibrary(editCategory || undefined);

  useEffect(() => {
    if (!item || !open) return;

    const normalizedCategory = item.category ? (findItemCategoryByText(item.category) ?? '') : '';
    setEditName(item.name);
    setEditDescription(item.description || '');
    setEditCategory(normalizedCategory);
    setEditBrand(isToolCategory(normalizedCategory) ? item.brand || '' : '');
    setEditPowerSource(item.power_source || '');
    setEditPhotoUrl(item.photo_url || null);
    setPhotoTab(item.photo_url && isLibraryUrl(item.photo_url) ? 'library' : 'upload');
    setEditCirculationMode(item.circulation_mode);
    setOriginalCategory(normalizedCategory);
    setEditCategoryDetails(createEmptyCategoryDetailsState());
    setAdditionalDetailsOpen(true);

    if (normalizedCategory) {
      loadCategoryDetails(item.id, normalizedCategory)
        .then(setEditCategoryDetails)
        .catch((error) => {
          toast.error('Could not load category details', { description: (error as Error).message });
        });
    }
  }, [item?.id, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || !editName.trim()) return;

    try {
      const detailValidationError = validateCategoryDetails(editCategory, editCategoryDetails);
      if (detailValidationError) {
        throw new Error(detailValidationError);
      }

      await updateItem.mutateAsync({
        id: item.id,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        category: editCategory || undefined,
        brand: isToolCategory(editCategory) ? editBrand.trim() || undefined : null,
        powerSource: isToolCategory(editCategory) ? editPowerSource || undefined : null,
        photoUrl: editPhotoUrl || undefined,
        circulationMode: editCirculationMode,
      });

      // If the category changed, delete the old category's detail row first.
      // Without this step, the previous category's detail table would keep a
      // stale row for this item_id, wasting space and causing confusion if
      // the category is ever switched back.
      if (originalCategory && originalCategory !== editCategory) {
        await deleteCategoryDetails(item.id, originalCategory);
      }

      await saveCategoryDetails(item.id, editCategory, editCategoryDetails);
      toast.success('Item updated!');
      onOpenChange(false);
    } catch {
      // Error toast handled by useUpdateItem onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogDescription>Update the details for "{item?.name}".</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Quick-Add section */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              placeholder="e.g., Drill, Harry Potter book"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-category">Category</Label>
            <Select
              value={editCategory}
              onValueChange={(category) => {
                const nextCategory = category as ItemCategory;
                setEditCategory(nextCategory);
                if (!isToolCategory(nextCategory)) {
                  setEditBrand('');
                  setEditPowerSource('');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {ITEM_CATEGORY_OPTIONS.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Photo</Label>
            {isToolCategory(editCategory) && libraryItems.length > 0 ? (
              <>
                <PhotoSourceTabs activeTab={photoTab} onTabChange={setPhotoTab} />
                {photoTab === 'upload' ? (
                  <ImageUpload
                    value={editPhotoUrl && !isLibraryUrl(editPhotoUrl) ? editPhotoUrl : null}
                    onChange={setEditPhotoUrl}
                  />
                ) : (
                  <PhotoLibraryPicker
                    category={editCategory}
                    onSelect={setEditPhotoUrl}
                    selectedUrl={editPhotoUrl && isLibraryUrl(editPhotoUrl) ? editPhotoUrl : undefined}
                  />
                )}
              </>
            ) : (
              <ImageUpload value={editPhotoUrl} onChange={setEditPhotoUrl} />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-circulationMode">Circulation Rules</Label>
            <Select
              value={editCirculationMode}
              onValueChange={(val) => setEditCirculationMode(val as CirculationMode)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select circulation mode" />
              </SelectTrigger>
              <SelectContent>
                {CIRCULATION_MODES.map((cm) => (
                  <SelectItem key={cm.value} value={cm.value}>
                    <div className="flex flex-col">
                      <span>{cm.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {CIRCULATION_MODES.find((cm) => cm.value === editCirculationMode)?.description}
            </p>
          </div>

          {/* Additional Details toggle — expanded by default when editing */}
          <button
            type="button"
            onClick={() => setAdditionalDetailsOpen((prev) => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '12px 16px',
              background: T.surfaceSubtle,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.sm,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Additional details</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                color: T.textMuted,
                transform: additionalDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease',
                flexShrink: 0,
              }}
            >
              <path d="M 4 6 L 8 10 L 12 6" />
            </svg>
          </button>

          {/* Additional Details section */}
          {additionalDetailsOpen && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description (optional)</Label>
                <Input
                  id="edit-description"
                  placeholder="Any details about the item"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  maxLength={2000}
                />
              </div>
              {isToolCategory(editCategory) && (
                <div className="space-y-2">
                  <Label htmlFor="edit-brand">Brand (optional)</Label>
                  <Input
                    id="edit-brand"
                    placeholder="e.g., DeWalt"
                    value={editBrand}
                    onChange={(e) => setEditBrand(e.target.value)}
                    maxLength={100}
                  />
                </div>
              )}
              {isToolCategory(editCategory) && (
                <div className="space-y-2">
                  <Label htmlFor="edit-powerSource">Power Source</Label>
                  <Select
                    value={editPowerSource}
                    onValueChange={(val) => setEditPowerSource(val as PowerSource)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select power source" />
                    </SelectTrigger>
                    <SelectContent>
                      {POWER_SOURCES.map((ps) => (
                        <SelectItem key={ps.value} value={ps.value}>
                          {ps.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editCategory && !isToolCategory(editCategory) && (
                <CategoryDetailsFields
                  category={editCategory}
                  details={editCategoryDetails}
                  onChange={setEditCategoryDetails}
                  idPrefix="group-edit-"
                />
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={updateItem.isPending}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: RADIUS.md,
              background: updateItem.isPending ? T.surfaceSubtle : T.accent,
              color: updateItem.isPending ? T.textMuted : T.white,
              border: updateItem.isPending ? `1px solid ${T.border}` : 'none',
              fontSize: 15,
              fontWeight: 600,
              cursor: updateItem.isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: updateItem.isPending ? 'none' : SHADOW.btn.accent,
            }}
          >
            {updateItem.isPending ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
